import { TrackingPixelEventSender } from './tracking-pixel';
import BaseEventModel from '../events/base-event-model';

const PII_PROPERTIES: string[] = [
  'screenResolution',
  'utmSource',
  'utmMedium',
  'utmCampaign',
  'utmTerm',
  'utmContent',
  'utmAdSet',
  'borderfreeProperties',
  'VWO Experiment ID',
  'VWO Campaign Name',
  'VWO Variation ID',
  'VWO Variation Name',
];
const STORAGE_KEY = 'GlobalE_Analytics_Queue';

export const EXCLUDED_CHECKOUT_EVENT_NAMES_WHEN_PII_DISABLED = ['express_from_cart_fallback', 'PreCheckoutExpressEligibility'];

export class DelayedTrackingPixelEventSender extends TrackingPixelEventSender {
  async send(event: BaseEventModel): Promise<void> {
    this.addToQueue(event);
    return Promise.resolve();
  }

  async releaseQueue(isPiiDataEnabled: boolean, isConsentGranted?: boolean) {
    const queue = this.getQueue();
    const eventsToRelease = isPiiDataEnabled
      ? queue
      : queue.filter(
          (event) =>
            event.category === 'Checkout Funnel' &&
            !EXCLUDED_CHECKOUT_EVENT_NAMES_WHEN_PII_DISABLED.includes(event.eventName),
        );
    const options = { isPiiDataEnabled, isConsentGranted };
    const actions = eventsToRelease.map((event) => this.releaseEvent(event, options));
    await Promise.allSettled(actions);
    DelayedTrackingPixelEventSender.clearQueue();
  }

  // Static because the queue outlives the sender that filled it: after a country switch away from a
  // consent-enforced country the SDK runs with a plain TrackingPixelEventSender, and the leftover
  // queue still has to be dropped (CORE-194518).
  static clearQueue(): void {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.error('Failed to clear session storage queue:', error);
    }
  }

  private addToQueue(event: BaseEventModel): void {
    try {
      const queue = this.getQueue();
      queue.push(event);
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(queue));
    } catch (error) {
      console.error('Failed to add event to session storage queue:', error);
    }
  }

  private getQueue(): BaseEventModel[] {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Failed to retrieve queue from session storage:', error);
      return [];
    }
  }

  private async releaseEvent(
    event: BaseEventModel,
    options: { isPiiDataEnabled: boolean; isConsentGranted?: boolean },
  ): Promise<void> {
    if (!options.isPiiDataEnabled) {
      this.cleanUpEvent(event);
    }
    event.isConsentGranted = options.isConsentGranted;
    event.isPiiDataEnabled = options.isPiiDataEnabled;
    return super.send(event);
  }

  // This method mutates argument
  private cleanUpEvent(event: BaseEventModel): void {
    PII_PROPERTIES.forEach((piiProperty) => delete event[piiProperty]);
    // VWO fields live in event.data (Checkout Funnel customData), not at top level
    const eventWithData = event as BaseEventModel & { data?: object };
    if (eventWithData.data && typeof eventWithData.data === 'object') {
      PII_PROPERTIES.forEach((key) => delete (eventWithData.data as Record<string, unknown>)[key]);
    }
  }
}
